import i18n from '@yandex-int/i18n';
import { ApiError } from 'client/api';
import { FormErrors } from 'client/hocs/types';
import { AliceSkillSettings, InstallableAppSettings, SmartHomeSettings } from 'client/model/settings';
import { Channel } from 'client/model/skill';
import { AliceSkillFormSettings, InstallableAppFormSettings, SmartHomeFormSettings } from 'client/types';
import { TError } from 'client/types/errors';
import * as keyset from 'i18n/SettingsErrors';

import { isInstallableAppChannel } from '.';

const t = i18n(keyset);

const convertToFileSizeLabel = (error: string) =>
    `${t('upload-file-size')} ${(Number(error.split(' &gt;')?.[1]?.split(' ')?.[0]?.trim()) || 0) / (1024 * 1024)} ${t('megabytes')}`;

export const getStringFieldErrorMessage = <TFieldName extends keyof InstallableAppFormSettings>(
    fieldName: TFieldName,
    fieldValue: string,
    zeroLengthErrorMessage: string,
    maxLength: number,
    maxLengthErrorMessage: string,
): Partial<FormErrors<InstallableAppFormSettings>> => {
    if (!fieldValue?.length) return { [fieldName]: zeroLengthErrorMessage };
    if (maxLength && fieldValue.length > maxLength) return { [fieldName]: maxLengthErrorMessage };
    return { [fieldName]: '' };
};

export const translateError = (error?: string) => {
    if (error?.includes('Upload file size')) {
        return convertToFileSizeLabel(error);
    }
    if (error === 'Need .html file') {
        return t('need-html-file');
    }
    if (error === 'Logo is too large') {
        return t('file-size-limit');
    }
    if (error === 'Need .apk file') {
        return t('need-apk-file');
    }
    if (error === 'Network Error') return t('network');
    // "Saved package name com.ecloud.eshare.server and package name from apk com.onelike.app are not same"
    if (error?.includes('Saved package name')) {
        return error
            .replace('Saved package', t('saved-package-name'))
            .replace('and package name from apk', t('and-uploaded'))
            .replace('are not same', t('not-same'));
    }
    return error;
};

export type AppStoreErrorArg = Record<string, string | number>;

const pickArg = (arg: AppStoreErrorArg | undefined, ...keys: string[]): string => {
    if (!arg) return '';
    for (const key of keys) {
        const value = arg[key];
        if (value !== undefined && value !== null) return String(value);
    }
    return '';
};

const mapAppStoreTypedErrorToTranslate: Record<TError, (arg?: AppStoreErrorArg) => string> = {
    VALIDATION_ERROR: () => t('validation-error'),
    APP_VERSION_CODE_DOWNGRADE: () => t('app-version-downgrade'),
    SAME_AS_PUBLISHED_APP_VERSION_CODE: () => t('same-as-published-app-version-code'),
    NO_ESIA_PROFILE: () => t('need-esia-profile-text'),
    PACKAGE_NAME_DIFF: () => t('package-name-diff'),
    PACKAGE_NAME_ALREADY_TAKEN: () => t('package-name-already-exist'),
    APK_ILLEGAL_PERMISSIONS: () => t('apk-illegal-permission'),
    INVALID_STRUCTURED_EXAMPLE: () => t('invalid-structured-example'),
    INVALID_STRUCTURED_EXAMPLE_HAS_ACTIVATION_NAME: () => t('invalid-structured-example-has-activation-name'),
    APP_NAME_IS_TAKEN: () => t('app-name-already-exist'),
    APP_NAME_MIN_WORDS_LIMIT: () => t('app-name-min-words-limit'),
    APP_NAME_MAX_WORDS_LIMIT: () => t('app-name-max-words-limit'),
    INVALID_URL: () => t('validation-url'),
    ACTIVATIONS_PHRASES_SAME: () => t('activation-name-is-the-same'),
    ACTIVATIONS_PHRASES_GENERAL: () => t('too-common-phrases'),
    ACTIVATIONS_PHRASES_MIN_WORDS_LIMIT: () => t('activation-phrases-min-words-limit'),
    ACTIVATIONS_PHRASES_MAX_WORDS_LIMIT: () => t('activation-phrases-max-words-limit'),
    ACTIVATIONS_PHRASES_IS_TAKEN: () => t('activation-name-already-registered'),
    CLOUD_FUNCTIONS_ARE_NOT_ALLOWED_FOR_OFFICIAL_SKILLS: () => t('cloud-functions-are-not-allowed-for-official-skills'),
    MAX_LENGTH_LIMIT: () => t('max-length-limit'),
    FILE_SIZE_LIMIT: () => t('file-size-limit'),
    FILE_WRONG_FORMAT: () => t('file-wrong-format'),
    FILE_NAME_IS_ABSENT: () => t('file-name-is-absent'),
    INTENT_EMPTY_FORM_NAME: () => t('intent-empty-form-name'),
    INTENT_FORM_NAME_UNIQUE: () => t('intent-form-name-unique'),
    SHARE_LIMIT_EXCEEDED: () => t('share-limit-exceed'),
    PORGANISATION_IS_NOT_VERIFIED: () => t('porg-verification-required'),
    NOT_ALL_DEVICES_SUPPORTED: (arg) =>
        t('not-all-devices-supported', { sdkVersion: pickArg(arg, 'sdkVersion', 'sdk_version') }),
    MIN_SDK_VERSION_NOT_SUPPORTED: () => t('min-sdk-version-not-supported'),
    APK_PARSE_ERROR: () => t('apk-parse-error'),
    APK_CHECK_FAILED: () => t('apk-check-failed'),
    ALICE_AGENT_APP_AGENT_NAME_INVALID_FORMAT: () => t('validation-agent-name-format'),
};

export const translateAppStoreError = (msg = '', arg?: AppStoreErrorArg) =>
    mapAppStoreTypedErrorToTranslate[msg as TError]?.(arg) ||
    msg ||
    mapAppStoreTypedErrorToTranslate.VALIDATION_ERROR();

export const mapApiErrorsToFormErrors = (
    message?: string,
): FormErrors<InstallableAppFormSettings & { common?: string; network?: string; esia?: string }> => {
    if (!message || typeof message !== 'string') return {};
    if (message === 'Empty platform list') return { apkInfosWithTargets: [t('apk-empty-platform-list')] };
    if (message === 'wrong package name format') return { packageName: t('wrong-package-name') };
    if (message === 'package name is missing') return { packageName: t('package-name-missing') };
    if (message === 'app name is quoted') return { name: t('name-quouted') };
    if (message === 'contains forbidden word') return { name: t('forbidden-words') };
    if (message === 'Unknown error') return { common: t('unknown-error') };
    if (message === 'Network Error') return { network: t('network-error') };
    if (message === 'Failed to verify url') return { brandVerificationWebsite: t('failed-to-verify-url') };
    if (message) return { common: message };

    return {};
};

const getSafeError = (structuredError: object | string | undefined | null): string => {
    if (!structuredError) return '';
    if (typeof structuredError === 'string') return structuredError;
    if (typeof structuredError === 'object') return Object.values(structuredError).join(', ');
    return '';
};

const mapInstallableAppSettingsErrorsToValuesErrors = (
    errors: InstallableAppSettings,
): FormErrors<InstallableAppFormSettings & { common?: string }> => {
    try {
        return {
            name: errors.name,
            activationPhrases: errors?.activationPhrases,
            secondaryTitle: errors?.secondaryTitle,
            description: errors?.publishingSettings?.description,
            ageRestriction: errors?.ageRestriction,
            email: errors?.publishingSettings?.email,
            brandVerificationWebsite: errors?.publishingSettings?.brandVerificationWebsite,
            packageName: errors?.packageName,
            apkInfosWithTargets: errors?.apkInfosWithTargets?.map?.((apkErrors) => getSafeError(apkErrors)),
            secondaryLogoId: errors?.secondaryLogoId || errors?.secondaryLogoUrl || undefined,
            screenshots: errors?.screenshots?.map?.((screenshotError) => getSafeError(screenshotError))?.join?.(', '),
            eulaAgreement: getSafeError(errors?.eulaAgreement),
            privacyAgreement: getSafeError(errors.privacyAgreement),
        };
    } catch (err) {
        console.error(err);
        return {};
    }
};

const mapSkillSettingsErrorsToValuesErrors = (
    errors: AliceSkillSettings,
): FormErrors<AliceSkillFormSettings & { common?: string }> => {
    try {
        return {
            name: errors.name,
            activationPhrases: errors?.activationPhrases,
            uri: errors?.backendSettings?.uri,
            functionId: errors?.backendSettings?.functionId || errors?.backendSettings?.backendType,
            structuredExamples: errors?.publishingSettings?.structuredExamples?.map?.((exampleError) =>
                getSafeError(exampleError),
            ),
            developerName: errors?.publishingSettings?.developerName,
            email: errors?.publishingSettings?.email,
            brandVerificationWebsite: errors?.publishingSettings?.brandVerificationWebsite,
            noteForModerator: errors?.noteForModerator,
            logoId: errors?.logoId || errors?.logoUrl || getSafeError(errors?.logo2),
            rsyPlatformId: errors?.rsyPlatformId,
            description: errors?.publishingSettings?.description,
        };
    } catch (err) {
        console.error(err);
        return {};
    }
};

const mapSmartHomeSettingsErrorsToValuesErrors = (
    errors: SmartHomeSettings,
): FormErrors<SmartHomeFormSettings & { common?: string }> => {
    try {
        return {
            supportedLanguages: Object.values(errors?.publishingSettings?.multilingualSettings || {}).map(
                (multilingualError) => JSON.stringify(multilingualError),
            ),
            selectedRegions: errors?.selectedRegions,
            name: errors?.name,
            secondaryTitle: errors?.publishingSettings?.secondaryTitle,
            developerName: errors?.publishingSettings?.developerName,
            email: errors?.publishingSettings?.email,
            isTrustedSmartHomeSkill: errors?.isTrustedSmartHomeSkill ? String(errors?.isTrustedSmartHomeSkill) : '',
            brandVerificationWebsite: errors?.publishingSettings?.brandVerificationWebsite,
            noteForModerator: errors?.noteForModerator,
            androidDeepLink: getSafeError(errors?.publishingSettings?.smartHome?.deepLinks?.android),
            iosDeepLink: getSafeError(errors?.publishingSettings?.smartHome?.deepLinks?.ios),
        };
    } catch (err) {
        console.error(err);
        return {};
    }
};

type ErrorMessageMapper = (key: string, value: string) => string;

export const convertToErrors = <T>(
    formErrors: FormErrors<T> = {} as FormErrors<T>,
    messageMapper: ErrorMessageMapper = (_key, value) => String(value),
): string[] => {
    return Object.entries(formErrors || {})
        .filter(([_key, value]) => (Array.isArray(value) ? !!(value as string[]).filter(Boolean).length : !!value))
        .map(([key, value]) =>
            messageMapper(
                key,
                Array.isArray(value) ? (value as string[]).filter(Boolean).join(' ') : (value as string),
            ),
        );
};

const NON_FIELD_ERROR_KEYS = new Set(['common', 'network']);

export const hasFieldErrors = <T>(formErrors: FormErrors<T> = {} as FormErrors<T>): boolean => {
    return Object.entries(formErrors || {}).some(
        ([key, value]) =>
            !NON_FIELD_ERROR_KEYS.has(key) && (Array.isArray(value) ? (value as string[]).some(Boolean) : !!value),
    );
};

export const createUpdateError = (error: unknown, channel: Channel) => {
    let formError: FormErrors<InstallableAppFormSettings & { common?: string }> = {};
    if (error instanceof ApiError && error.fields) {
        let errorsInForm = error.fields;
        if (isInstallableAppChannel(channel)) {
            errorsInForm = mapInstallableAppSettingsErrorsToValuesErrors(error.fields as InstallableAppSettings);
        }
        if (channel === Channel.AliceSkill) {
            errorsInForm = mapSkillSettingsErrorsToValuesErrors(error.fields as unknown as AliceSkillSettings);
        }
        if (channel === Channel.SmartHome) {
            errorsInForm = mapSmartHomeSettingsErrorsToValuesErrors(error.fields as unknown as SmartHomeSettings);
        }
        const commonError =
            Object.values(errorsInForm).filter(Boolean).length !== Object.values(error.fields).filter(Boolean).length
                ? translateError(error.message)
                : undefined;
        formError = {
            common: commonError,
            ...errorsInForm,
        };
    } else if (!!error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
        formError = mapApiErrorsToFormErrors(error.message);
    } else if (!!error && typeof error === 'string') {
        return { common: translateError(error) };
    }
    return formError;
};
